import { Component, Input, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { WalletBalanceSummaryService } from 'src/app/services/wallet.balance.summary.service';
import { FormUtils } from 'src/app/utils/form.utils';

@Component({
    selector: 'app-wallet-balance',
    templateUrl: './wallet-balance.component.html',
    styleUrls: ['./wallet-balance.component.css']
})
export class WalletBalanceComponent implements OnInit {
    @Input() supplierId!: number | undefined;

    id!: string;

    availableBalance = 0;
    cashbacksAllocated = 0;
    pendingCashbackAllocations = 0;
    warningThreshold = 0;
    cutoffThreshold = 0;

    belowCutoffThreshold = false;
    belowWarningThreshold = false;

    isEditingWarningThreshold = false;
    isEditingCutoffThreshold = false;

    isLoadingWalletSummary = false;
    isSavingWarningThreshold = false;
    isSavingCutoffThreshold = false;
    isRequestingTopup = false;

    thresholdForm!: FormGroup;

    sessionUser$ = this.loginService.sessionUser.asObservable();

    FormUtils = FormUtils;

    constructor(
        public loginService: LoginService,
        private fb: FormBuilder,
        private walletBalanceSummaryService: WalletBalanceSummaryService,
        private toasterService: ToastrService,
    ) { }

    ngOnInit(): void {
        this.buildThresholdForm();
        this.getWalletSummary();
    }

    get canRequestTopup(): boolean {
        return this.isBelowWarningThreshold || this.isBelowCutoffThreshold;
    }

    get isBelowCutoffThreshold(): boolean {
        return this.belowCutoffThreshold;
    }

    get isBelowWarningThreshold(): boolean {
        return this.belowWarningThreshold;
    }

    get isAvailableBalanceWarning(): boolean {
        return this.isBelowWarningThreshold || this.isBelowCutoffThreshold;
    }

    get availableBalanceCardClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card--warning': this.isBelowWarningThreshold,
            'wallet-balance-card--alert': this.isBelowCutoffThreshold,
        };
    }

    get availableBalanceValueClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card__value--warning': this.isBelowWarningThreshold,
            'wallet-balance-card__value--alert': this.isBelowCutoffThreshold,
        };
    }

    get warningThresholdCardClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card--warning': !this.isBelowCutoffThreshold && this.isBelowWarningThreshold,
            'wallet-balance-card--alert': this.isBelowCutoffThreshold,
        };
    }

    get warningThresholdValueClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card__value--warning': !this.isBelowCutoffThreshold && this.isBelowWarningThreshold,
            'wallet-balance-card__value--alert': this.isBelowCutoffThreshold,
        };
    }

    get cutoffThresholdCardClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card--alert': this.isBelowCutoffThreshold,
        };
    }

    get cutoffThresholdValueClasses(): { [klass: string]: boolean } {
        return {
            'wallet-balance-card__value--alert': this.isBelowCutoffThreshold,
        };
    }

    startWarningThresholdEdit(): void {
        this.syncThresholdForm();
        this.resetFieldState('warningThreshold');
        this.isEditingWarningThreshold = true;
    }

    startCutoffThresholdEdit(): void {
        this.syncThresholdForm();
        this.resetFieldState('cutoffThreshold');
        this.isEditingCutoffThreshold = true;
    }

    cancelWarningThresholdEdit(): void {
        this.isEditingWarningThreshold = false;
        this.syncThresholdForm();
        this.resetFieldState('warningThreshold');
    }

    cancelCutoffThresholdEdit(): void {
        this.isEditingCutoffThreshold = false;
        this.syncThresholdForm();
        this.resetFieldState('cutoffThreshold');
    }

    getWalletSummary(): void {
        console.log("Supplier ID:", this.supplierId); // Debug log to check supplierId value
        this.walletBalanceSummaryService
            .getWalletSummary(this.supplierId)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.applyWalletSummary(response?.result ?? {});
                },
                error: (error) => {
                    console.error('Failed to load wallet summary', error);
                },
            });
    }

    saveWarningThreshold(): void {
        FormUtils.validateFormField(this.thresholdForm, 'warningThreshold');
        this.thresholdForm.updateValueAndValidity();

        if (
            FormUtils.isFieldInValid(this.thresholdForm, 'warningThreshold') ||
            this.thresholdForm.hasError('warningBelowCutoff')
        ) {
            return;
        }

        const value = Number(this.thresholdForm.get('warningThreshold')?.value);

        this.isSavingWarningThreshold = true;
        this.thresholdForm.get('warningThreshold')?.disable({ emitEvent: false });

        this.walletBalanceSummaryService
            .updateWalletThresholds(this.supplierId, {
                warningThreshold: value,
                cutoffThreshold: this.cutoffThreshold,
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.applyWalletSummary(response?.result ?? {});
                    this.isEditingWarningThreshold = false;
                    this.isSavingWarningThreshold = false;
                    this.thresholdForm.get('warningThreshold')?.enable({ emitEvent: false });
                    this.toasterService.success('Warning threshold updated successfully', 'Success', {
                        closeButton: true,
                        progressBar: true,
                        tapToDismiss: true,
                    });

                },
                error: (error) => {
                    this.isSavingWarningThreshold = false;
                    this.thresholdForm.get('warningThreshold')?.enable({ emitEvent: false });
                    console.error('Failed to update warning threshold', error);
                },
            });
    }

    saveCutoffThreshold(): void {
        FormUtils.validateFormField(this.thresholdForm, 'cutoffThreshold');
        this.thresholdForm.updateValueAndValidity();

        if (
            FormUtils.isFieldInValid(this.thresholdForm, 'cutoffThreshold') ||
            this.thresholdForm.hasError('cutoffAboveWarning')
        ) {
            return;
        }

        const value = Number(this.thresholdForm.get('cutoffThreshold')?.value);

        this.isSavingCutoffThreshold = true;
        this.thresholdForm.get('cutoffThreshold')?.disable({ emitEvent: false });

        this.walletBalanceSummaryService
            .updateWalletThresholds(this.supplierId, {
                warningThreshold: this.warningThreshold,
                cutoffThreshold: value,
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.applyWalletSummary(response?.result ?? {});
                    this.isEditingCutoffThreshold = false;
                    this.isSavingCutoffThreshold = false;
                    this.thresholdForm.get('cutoffThreshold')?.enable({ emitEvent: false });
                    this.toasterService.success('Cutoff threshold updated successfully', 'Success', {
                        closeButton: true,
                        progressBar: true,
                        timeOut: 3000,
                        tapToDismiss: true,
                    });
                },
                error: (error) => {
                    this.isSavingCutoffThreshold = false;
                    this.thresholdForm.get('cutoffThreshold')?.enable({ emitEvent: false });
                    console.error('Failed to update cutoff threshold', error);
                },
            });
    }

    requestTopup(): void {
        if (!this.canRequestTopup || this.isRequestingTopup) {
            return;
        }

        this.isRequestingTopup = true;

        this.walletBalanceSummaryService
            .requestTopup(this.supplierId)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    this.isRequestingTopup = false;

                    const apiSuccess = !!response?.success;
                    const result = response?.result ?? {};
                    const emailSent = !!result?.emailSent;
                    const message = result?.message || 'Top-up notification processed';

                    if (!apiSuccess) {
                        this.toasterService.error(message, 'Error', {
                            closeButton: true,
                            progressBar: true,
                            timeOut: 4000,
                            tapToDismiss: true,
                        });
                        return;
                    }

                    if (emailSent) {
                        this.toasterService.success(message, 'Success', {
                            closeButton: true,
                            progressBar: true,
                            timeOut: 3000,
                            tapToDismiss: true,
                        });
                    } else {
                        this.toasterService.warning(message, 'Warning', {
                            closeButton: true,
                            progressBar: true,
                            timeOut: 5000,
                            tapToDismiss: true,
                        });
                    }
                },
                error: (error) => {
                    this.isRequestingTopup = false;
                    console.error('Failed to trigger top-up notification', error);

                    this.toasterService.error(
                        error?.error?.status?.message || 'Failed to trigger top-up notification',
                        'Error',
                        {
                            closeButton: true,
                            progressBar: true,
                            timeOut: 4000,
                            tapToDismiss: true,
                        }
                    );
                },
            });
    }

    private applyWalletSummary(response: any): void {
        this.availableBalance = Number(response?.availableBalance ?? 0);
        this.cashbacksAllocated = Number(response?.cashbacksAllocated ?? 0);
        this.pendingCashbackAllocations = Number(response?.pendingCashbackAllocations ?? 0);
        this.warningThreshold = Number(response?.warningThreshold ?? 0);
        this.cutoffThreshold = Number(response?.cutoffThreshold ?? 0);

        this.belowWarningThreshold = response?.belowWarningThreshold ?? false;
        this.belowCutoffThreshold = response?.belowCutoffThreshold ?? false;

        this.syncThresholdForm();
    }

    private buildThresholdForm(): void {
        this.thresholdForm = this.fb.group(
            {
                warningThreshold: [
                    this.warningThreshold,
                    [
                        Validators.required,
                        Validators.min(0),
                        this.numericValidator(),
                    ],
                ],
                cutoffThreshold: [
                    this.cutoffThreshold,
                    [
                        Validators.required,
                        Validators.min(0),
                        this.numericValidator(),
                    ],
                ],
            },
            {
                validators: [this.thresholdRelationshipValidator()],
            }
        );
    }

    private syncThresholdForm(): void {
        this.thresholdForm.patchValue(
            {
                warningThreshold: this.warningThreshold,
                cutoffThreshold: this.cutoffThreshold,
            },
            { emitEvent: false }
        );

        this.thresholdForm.updateValueAndValidity({ emitEvent: false });
    }

    private resetFieldState(field: string): void {
        const control = this.thresholdForm.get(field);

        if (control) {
            control.markAsPristine();
            control.markAsUntouched();
        }

        this.thresholdForm.updateValueAndValidity({ emitEvent: false });
    }

    private numericValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (value === null || value === undefined || value === '') {
                return null;
            }

            const parsed = Number(value);

            return Number.isFinite(parsed) ? null : { invalidNumber: true };
        };
    }

    private thresholdRelationshipValidator(): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const warningRaw = group.get('warningThreshold')?.value;
            const cutoffRaw = group.get('cutoffThreshold')?.value;

            if (
                warningRaw === null || warningRaw === undefined || warningRaw === '' ||
                cutoffRaw === null || cutoffRaw === undefined || cutoffRaw === ''
            ) {
                return null;
            }

            const warning = Number(warningRaw);
            const cutoff = Number(cutoffRaw);

            if (!Number.isFinite(warning) || !Number.isFinite(cutoff)) {
                return null;
            }

            if (warning > 0 && cutoff > 0 && warning < cutoff) {
                return {
                    warningBelowCutoff: true,
                    cutoffAboveWarning: true,
                };
            }

            return null;
        };
    }
}
